import { EmploymentStatusType } from 'enrollment-platform/pages/finance/api/dto/get-payment-assignment-detail.dto'
import { PaymentAssignmentRole } from './roles'

// Backend sends values like "MANUAL" / "AUTO" / "BOT"
export enum PaymentType {
  MANUAL = 'MANUAL',
  AUTO = 'AUTO',
  BOT = 'BOT',
}

export const PAYMENT_TYPE_LABELS: Record<PaymentType, string> = {
  [PaymentType.MANUAL]: 'Manual',
  [PaymentType.AUTO]: 'Auto',
  [PaymentType.BOT]: 'Bot',
}

export enum Priority {
  DEFAULT = 0,
  EE_INQUIRY = 1,
  HR_INQUIRY = 2,
  EXECUTIVE_INQUIRY = 3,
}

export const PRIORITY_LABELS: Record<Priority, string> = {
  [Priority.DEFAULT]: '-',
  [Priority.EE_INQUIRY]: 'EE Inquiry',
  [Priority.HR_INQUIRY]: 'HR Inquiry',
  [Priority.EXECUTIVE_INQUIRY]: 'Executive Inquiry',
}

export const PriorityList = [
  Priority.DEFAULT,
  Priority.EE_INQUIRY,
  Priority.HR_INQUIRY,
  Priority.EXECUTIVE_INQUIRY,
] as const

export enum PaymentAssignmentStatusId {
  PENDING_ASSIGNMENT = 'PENDING_ASSIGNMENT',
  PENDING_PAYMENT = 'PENDING_PAYMENT',
  PAYMENT_SUBMITTED = 'PAYMENT_SUBMITTED',
  // PENDING_RESOLVE_ISSUE is not in the API, but is used in the UI
  PENDING_RESOLVE_ISSUE = 'PENDING_RESOLVE_ISSUE',
  PENDING_AUTOPAY = 'PENDING_AUTOPAY',
  PAID_ESCALATION = 'PAID_ESCALATION',
  UNPAID_ESCALATION = 'UNPAID_ESCALATION',
  EE_PAID = 'EE_PAID',
  TERMINATED_EE = 'TERMINATED_EE',
  TERMINATED_PLAN = 'TERMINATED_PLAN',
  PAYMENT_CLEARED = 'PAYMENT_CLEARED',
  AP_CONFIRMED = 'AP_CONFIRMED',
  REATTEMPT = 'REATTEMPT',
  ESCALATION_FULLY_RESOLVED = 'ESCALATION_FULLY_RESOLVED',
  NEW_EFFECTIVE_DATE = 'NEW_EFFECTIVE_DATE',
  CARRIER_SHOW_PAYMENT = 'CARRIER_SHOW_PAYMENT',
  BOT_QUEUED = 'BOT_QUEUED',
  BOT_PROCESSING = 'BOT_PROCESSING',
  BOT_FAILED = 'BOT_FAILED',
  BOT_SUBMITTED = 'BOT_SUBMITTED',
  BOT_CLEARED = 'BOT_CLEARED',
  BOT_CANCELLED = 'BOT_CANCELLED',
}

export const PAYMENT_ASSIGNMENT_STATUS_LABELS: Record<
  PaymentAssignmentStatusId,
  string
> = {
  [PaymentAssignmentStatusId.PENDING_ASSIGNMENT]: 'Pending assignment',
  [PaymentAssignmentStatusId.PENDING_PAYMENT]: 'Pending payment',
  [PaymentAssignmentStatusId.PAYMENT_SUBMITTED]: 'Payment submitted',
  [PaymentAssignmentStatusId.PENDING_AUTOPAY]: 'Pending autopay',
  [PaymentAssignmentStatusId.PAID_ESCALATION]: 'Paid escalation',
  [PaymentAssignmentStatusId.UNPAID_ESCALATION]: 'Unpaid escalation',
  [PaymentAssignmentStatusId.EE_PAID]: 'EE paid',
  [PaymentAssignmentStatusId.TERMINATED_EE]: 'Terminated EE',
  [PaymentAssignmentStatusId.TERMINATED_PLAN]: 'Terminated plan',
  [PaymentAssignmentStatusId.PAYMENT_CLEARED]: 'Payment cleared',
  [PaymentAssignmentStatusId.AP_CONFIRMED]: 'AP confirmed',
  [PaymentAssignmentStatusId.REATTEMPT]: 'Reattempt',
  [PaymentAssignmentStatusId.ESCALATION_FULLY_RESOLVED]:
    'Escalation fully resolved',
  [PaymentAssignmentStatusId.NEW_EFFECTIVE_DATE]: 'New effective date',
  [PaymentAssignmentStatusId.CARRIER_SHOW_PAYMENT]:
    'Carrier shows payment made',
  [PaymentAssignmentStatusId.BOT_QUEUED]: 'Bot queued',
  [PaymentAssignmentStatusId.BOT_PROCESSING]: 'Bot processing',
  [PaymentAssignmentStatusId.BOT_FAILED]: 'Bot failed',
  [PaymentAssignmentStatusId.BOT_SUBMITTED]: 'Bot submitted',
  [PaymentAssignmentStatusId.BOT_CLEARED]: 'Bot cleared',
  [PaymentAssignmentStatusId.PENDING_RESOLVE_ISSUE]: 'Pending resolve issue',
  [PaymentAssignmentStatusId.BOT_CANCELLED]: 'Bot cancelled',
}

export function getPaymentAssignmentStatusLabel(
  status?: string | PaymentAssignmentStatusId | null
): string {
  if (!status) return ''
  const label =
    PAYMENT_ASSIGNMENT_STATUS_LABELS[status as PaymentAssignmentStatusId]
  return label ?? status
}

export type PaymentStatus = PaymentAssignmentStatusId

export type Relationship =
  | 'Employee'
  | 'Spouse'
  | 'Domestic Partner'
  | 'Child'
  | 'Child of Domestic Partner'

export enum PersonRelationshipEnum {
  EMPLOYEE = 'Employee',
  SPOUSE = 'Spouse',
  DOMESTIC_PARTNER = 'Domestic Partner',
  CHILD = 'Child',
  CHILD_OF_DOMESTIC_PARTNER = 'Child of Domestic Partner',
}

export const PERSON_RELATIONSHIP_LABELS: Record<
  PersonRelationshipEnum,
  string
> = {
  [PersonRelationshipEnum.EMPLOYEE]: 'Employee',
  [PersonRelationshipEnum.SPOUSE]: 'Spouse',
  [PersonRelationshipEnum.DOMESTIC_PARTNER]: 'Domestic Partner',
  [PersonRelationshipEnum.CHILD]: 'Child',
  [PersonRelationshipEnum.CHILD_OF_DOMESTIC_PARTNER]:
    'Child of Domestic Partner',
}

export interface PaymentMethod {
  id: string
  methodType: 'Online' | 'IVR' | 'Broker Portal' | 'Live Rep'
  paymentMethods?: string // e.g., "VCN/ACH"
  primaryPaymentMethod?: string // e.g., "VCN", "VCN AP", "ACH"
  paymentSources?: string // e.g., "Online/IVR"

  // Broker Portal fields
  brokerPortalLink?: string
  brokerPortalUsernameMK?: string
  brokerPortalPasswordMK?: string
  brokerPortalUsernameMC?: string
  brokerPortalPasswordMC?: string

  // Online Payment fields
  onlinePaymentPortal?: string
  onlinePaymentNeeds?: string
  onlinePaymentUsername?: string
  onlinePaymentPassword?: string
  premiumOwedGiven?: boolean
  customizedPaymentAmount?: boolean

  // IVR Payment fields
  ivrPaymentLine?: string
  ivrPaymentNeeds?: string
  premiumOwedGivenIVR?: boolean
  customizedPaymentAmountIVR?: boolean

  // Autopay fields
  autopayInfo?: string
  autopayPaymentSource?: string
  autopayPossibleDates?: string
}

export type ActionType = 'Status Updated' | 'Payment Info Updated' | 'Unknown'

export type Actor =
  | 'Checker'
  | 'Maker'
  | 'ESCALATOR'
  | 'BOT_IVR'
  | 'BOT_API'
  | 'BOT_WEB_AGENT'
  | 'RECONCILIATION_SYSTEM'
  | 'Unknown'

export const ROLE_TO_ACTOR: Record<string, Actor> = {
  CHECKER: 'Checker',
  MAKER: 'Maker',
  BOT_IVR: 'BOT_IVR',
  BOT_API: 'BOT_API',
  BOT_WEB_AGENT: 'BOT_WEB_AGENT',
  RECONCILIATION_SYSTEM: 'RECONCILIATION_SYSTEM',
  UNKNOWN: 'Unknown',
}

export function roleToActor(role: string | undefined): Actor {
  if (role == null || role === '') return 'Unknown'
  const normalized = role.toUpperCase().trim()
  return ROLE_TO_ACTOR[normalized] ?? 'Unknown'
}

export interface PaymentHistoryEntry {
  id: string
  timestamp: string
  actionType: ActionType
  hasCreatedByUser: boolean
  actor: Actor
  actorName?: string
  performedByRole?: string
  assigneeName?: string
  escalatorName?: string
  performedByEmail?: string
  assigneeEmail?: string
  escalatorEmail?: string
  priority?: number
  description?: string
  statusBefore?: string
  statusAfter: string
  notes?: string
  reason?: string
  /** Generic metadata from benefit event (e.g. priority, didSetUpAutopay, dates). */
  metadata?: Record<string, unknown>
  /** Payment field snapshot for pa.payment_info_update events */
  snapshot?: Record<string, string | boolean | null | undefined>
}

export interface PaymentCase {
  id: number
  benefitId: string
  month?: number
  year?: number
  priority?: Priority | null
  expectedCost?: string | number | null
  actualCost?: string | number | null
  clearedCost?: string | number | null
  currentExpectedCost?: string | number | null
  /** List API returns string; detail may use PaymentType enum. */
  paymentType?: PaymentType | string | null
  paymentStatus?: {
    id?: PaymentAssignmentStatusId | null
    description?: string | null
    createdAt?: string
    updatedAt?: string
  } | null
  assignee?: unknown
  assigneeId?: number | null
  assigneeName?: string
  /** List API returns string; detail uses 'Maker' | 'Checker'. */
  assigneeRole?:
    | PaymentAssignmentRole.MAKER
    | PaymentAssignmentRole.CHECKER
    | string
  escalatorId?: number | null
  escalatorName?: string | null
  createdAt?: string
  updatedAt?: string
  // Flattened from list API (PaymentAssignmentItemDto)
  employeeName?: string
  companyName?: string
  companyEmployeeId?: string | null
  carrierName?: string
  /** Group ID (can be null). */
  groupId?: string | null
  /** List API returns these at top level; detail may only nest under benefit. */
  benefitEffectiveDate?: string | null
  benefitEndDate?: string | null
  isTestUser?: boolean | null
  escalationReason?: string | null
  confirmationNumber?: string | null
  ticket?: string | null
  actualPaymentDate?: string | null
  didSetUpAutopay?: boolean | string | null
  /** List API: who submitted payment. */
  paymentSubmittedBy?: string | null
  needsBinderPayment?: boolean | null
  isBinderPayment?: boolean | null
  // Legacy nested shape (optional, for backward compat)
  /** Detail API includes carrierApplication. */
  benefit?: {
    id: string
    employeePublicKey: string
    employeeName: string
    companyName: string
    carrierName?: string | null
    benefitEffectiveDate?: string | null
    benefitEndDate?: string | null
    person?: { id: string }
    /** From detail API; same shape as get-payment-assignment-detail CarrierApplication (PascalCase or camelCase). */
    carrierApplication?: Record<string, unknown> | null
  } | null
  paymentMethods?: PaymentMethod[]

  paymentCarrier?:
    | {
        id: number
        name: string
        issuerId?: string | null
        state?: string | null
        fullname?: string | null
      }
    | undefined

  insuredPerson?: {
    id: string
    smoker: boolean
    lastName: string
    firstName: string
    postalCode: string
    ratingArea: number
    relationship: Relationship
    stateProvince: string
    employmentStatus?: EmploymentStatusType
  }
  activityLogs?: PaymentHistoryEntry[]

  /** List of payment assignments that were migrated to this one (children). */
  migratedFromList?: PaymentCase[]

  // Payment method / ACH (from benefit.carrierApplication, detail only)
  /** Top-level alias returned by the list API. */
  primaryPaymentMethod?: string
  carrierApplicationPrimaryPaymentMethod?: string
  carrierApplicationPrimaryACHAccountNumber?: string
  carrierApplicationPrimaryACHAccountType?: string
  carrierApplicationPrimaryACHAccountUse?: string
  /** WEX merchant log id or Marqeta card key — from benefit detail when present. */
  virtualCardId?: string
  /** Wire value e.g. WEX, MARQETA — from benefit detail when present. */
  virtualCardType?: string
  /**
   * Unified card identifier for active card matching:
   * - Marqeta: Card Public Key
   * - WEX: Merchant Log ID
   */
  virtualCardKey?: string
}

/** Statuses that block "Submit payment" (already submitted, in progress, or terminal/completed). */
const PAYMENT_BOT_BLOCKED_STATUSES: PaymentAssignmentStatusId[] = [
  PaymentAssignmentStatusId.PAYMENT_SUBMITTED,
  PaymentAssignmentStatusId.BOT_QUEUED,
  PaymentAssignmentStatusId.BOT_PROCESSING,
  PaymentAssignmentStatusId.BOT_SUBMITTED,
  PaymentAssignmentStatusId.BOT_CLEARED,
  PaymentAssignmentStatusId.PAYMENT_CLEARED,
  PaymentAssignmentStatusId.EE_PAID,
  PaymentAssignmentStatusId.AP_CONFIRMED,
  PaymentAssignmentStatusId.TERMINATED_EE,
  PaymentAssignmentStatusId.TERMINATED_PLAN,
]

const PAYMENT_BOT_CANCELLABLE_STATUSES: PaymentAssignmentStatusId[] = [
  PaymentAssignmentStatusId.BOT_QUEUED,
]

/** Whether the payment bot queue can be cancelled for this case (Checker/Escalator). */
export function isPaymentBotCancellable(
  paymentCase: PaymentCase | null | undefined
): boolean {
  if (!paymentCase) return false
  const statusId = paymentCase.paymentStatus?.id
  if (!statusId) return false
  return (
    paymentCase.paymentType === PaymentType.BOT &&
    PAYMENT_BOT_CANCELLABLE_STATUSES.includes(statusId)
  )
}

/** Whether the "Run bot" / "Submit payment" button is runnable for this payment case (BOT type, not blocked by status). */
export function isPaymentBotRunnable(
  paymentCase: PaymentCase | null | undefined
): boolean {
  if (!paymentCase) return false
  const statusId = paymentCase.paymentStatus?.id
  if (!statusId) return false
  return (
    paymentCase.paymentType === PaymentType.BOT &&
    !PAYMENT_BOT_BLOCKED_STATUSES.includes(statusId)
  )
}

/** Test user filter (API values: all, test_only, non_test_only). */
export enum TestUserFilter {
  ALL = 'all',
  TEST_ONLY = 'test_only',
  NON_TEST_ONLY = 'non_test_only',
}

/** Terminated employee filter (API values: all, terminated, not_terminated). */
export enum TerminatedFilter {
  ALL = 'all',
  TERMINATED = 'terminated',
  NOT_TERMINATED = 'not_terminated',
}

/** Filter value for "submitted by bot" in `paymentSubmittedByIds` (GET /payment-assignment). */
export const PAYMENT_SUBMITTED_BY_BOT_FILTER_ID = 0

export interface PaymentFilters {
  paymentPeriod?: string // Month picker value (YYYY-MM)
  /** Benefit plan effective date filter (inclusive range, YYYY-MM-DD). */
  benefitEffectiveDateFrom?: string
  benefitEffectiveDateTo?: string
  /** Benefit plan end date filter (inclusive range, YYYY-MM-DD). */
  benefitEndDateFrom?: string
  benefitEndDateTo?: string
  carrierNames: string[] // Multi-select (carrier names from API), empty = all
  paymentTypes: (PaymentType | string)[] // Multi-select, empty = all (allow string for SELECT_ALL_VALUE)
  priorityLevels: Priority[] // Multi-select, empty = all
  statuses: string[] // Multi-select, dynamic based on payment type
  assigneeIds: number[] // Multi-select, empty = all
  escalatorIds: number[] // Multi-select, empty = all
  /** Multi-select user ids (makers + checkers + bot); empty = all. Use PAYMENT_SUBMITTED_BY_BOT_FILTER_ID for bot. */
  paymentSubmittedByIds: number[]
  clientPublicKeys?: string[] // Multi-select (client public keys from administered customers)
  search?: string // Free text
  testUserFilter?: TestUserFilter
  terminatedFilter?: TerminatedFilter
  relationships?: PersonRelationshipEnum[]
  binderPayment?: boolean
  /** Multi-select carrier application primary payment methods (ACH, VCN, GROUP ACH, Check); empty = all. */
  primaryPaymentMethods?: string[]
  /** When true, only shows parent assignments that have migrated children (Benefit ID changed). */
  onlyMigrated?: boolean
}

/** Payment Assignment list sort column (API sortBy param). Shared by table, hook, and DTO. */
export enum PaymentAssignmentSortBy {
  ID = 'id',
  PRIORITY = 'priority',
  CARRIER_NAME = 'carrierName',
  GROUP_ID = 'groupId',
  BENEFIT_ID = 'benefitId',
  COMPANY_EMPLOYEE_ID = 'companyEmployeeId',
  COMPANY_NAME = 'companyName',
  EMPLOYEE_NAME = 'employeeName',
  EXPECTED_COST = 'expectedCost',
  CLEARED_COST = 'clearedCost',
  CURRENT_EXPECTED_COST = 'currentExpectedCost',
  PAYMENT_TYPE = 'paymentType',
  ASSIGNEE_ID = 'assigneeId',
  ESCALATOR_ID = 'escalatorId',
  STATUS_ID = 'statusId',
  INSURED_PERSON_NAME = 'insuredPersonName',
  ESCALATION_REASON = 'escalationReason',
  TICKET = 'ticket',
  BENEFIT_EFFECTIVE_DATE = 'benefitEffectiveDate',
  BENEFIT_END_DATE = 'benefitEndDate',
  INSURED_PERSON_RELATIONSHIP = 'insuredPersonRelationship',
}

export type PaymentAssignmentSortOrder = 'asc' | 'desc'

export interface PaginationState {
  page: number
  pageSize: number
  total: number
}

export interface ScoreboardMetrics {
  totalActiveCases: number
  assignedToMe: number
  pendingAction: number
  escalatedToChecker: number
  completedToday: number
}

export const NOT_AVAILABLE = 'N/A'
